import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { usePageTitle } from '@/hooks/use-page-title';
import { useApplicationDetail } from '@/hooks/use-entity-detail';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/hooks/use-permissions';
import { Permissions } from '@/lib/permissions';
import { useHeaderAction } from '@/lib/header-action-context';
import { useConnectStatus, type ConnectAccount } from '@/hooks/use-connect-status';
import { api } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConnectPanel } from '@/components/billing/ConnectPanel';
import { toast } from 'sonner';
import {
    CreditCard,
    RefreshCw,
    ArrowRight,
    Building2,
    CheckCircle2,
    Settings,
} from 'lucide-react';

interface ResolvedStatus {
    account: ConnectAccount;
    source: 'application' | 'organization';
}

export default function ApplicationBillingPage() {
    const { appKey } = useParams();
    const { currentOrganization } = useAuth();
    const { hasPermission } = usePermissions();
    const canManage = hasPermission(Permissions.APP_BILLING_MANAGE);
    const { setHeaderTitle } = useHeaderAction();
    const { data: appData, isLoading: appLoading } = useApplicationDetail(appKey!);

    usePageTitle(`Billing - ${appKey}`);

    const app = appData?.application as {
        id?: string;
        name?: string;
        app_key?: string;
    } | undefined;
    const appId = app?.id;
    const appName = app?.name ?? appKey ?? 'Application';

    const [onboarding, setOnboarding] = useState(false);
    const [wantsCustom, setWantsCustom] = useState(false);

    const [resolvedStatus, setResolvedStatus] = useState<ResolvedStatus | null>(null);
    const [resolvedLoading, setResolvedLoading] = useState(true);

    const fetchResolvedStatus = useCallback(async () => {
        if (!appId) return;
        setResolvedLoading(true);
        try {
            const { data } = await api.get<ResolvedStatus>(`/billing/connect/status/app/${appId}/resolved`);
            setResolvedStatus(data);
            if (data.source === 'application') {
                setWantsCustom(true);
            }
        } catch {
            setResolvedStatus(null);
        } finally {
            setResolvedLoading(false);
        }
    }, [appId]);

    useEffect(() => {
        if (appId) fetchResolvedStatus();
    }, [appId, fetchResolvedStatus]);

    const hasAppLevelAccount = resolvedStatus?.source === 'application';
    const usingOrgDefault = resolvedStatus != null && resolvedStatus.source === 'organization';
    const noAccountAtAll = !resolvedLoading && resolvedStatus == null;

    const statusUrl = wantsCustom && appId ? `/billing/connect/status/app/${appId}` : null;
    const accountSessionPayload = useMemo(
        () => (appId && currentOrganization?.id
            ? { org_id: currentOrganization.id, application_id: appId }
            : null),
        [appId, currentOrganization?.id],
    );

    const { connectState, fetchConnectStatus, stripeConnectInstance } = useConnectStatus({
        statusUrl,
        accountSessionPayload,
    });

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                    <CreditCard size={16} />
                </div>
            ),
            title: 'Billing',
            subtitle: `Connect your Stripe account to receive payouts for ${appName}.`,
        });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle, appName]);

    const handleSetUpPayments = async () => {
        if (!currentOrganization?.id || !appId) return;
        setOnboarding(true);
        try {
            await api.post('/billing/connect/onboard', {
                org_name: currentOrganization.name ?? 'Organization',
                application_id: appId,
            });
            await fetchConnectStatus();
            await fetchResolvedStatus();
        } catch {
            toast.error('Failed to start Stripe onboarding');
            setOnboarding(false);
        }
    };

    const handleSwitchToCustom = () => {
        setWantsCustom(true);
    };

    if (appLoading) {
        return (
            <div className="p-6">
                <Card className="p-8">
                    <div className="flex items-center justify-center gap-3">
                        <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                        <p className="text-slate-500">Loading application...</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (resolvedLoading) {
        return (
            <div className="p-6">
                <Card className="p-8">
                    <div className="flex items-center justify-center gap-3">
                        <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                        <p className="text-slate-500">Loading billing configuration...</p>
                    </div>
                </Card>
            </div>
        );
    }

    if ((usingOrgDefault || noAccountAtAll) && !wantsCustom) {
        return (
            <div className="p-6 space-y-6">
                <Card className="border-white/30 bg-white/40 shadow-sm">
                    <CardHeader>
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 border border-blue-200">
                                <Building2 size={24} className="text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-3">
                                    <CardTitle className="text-lg">Using Organization Default</CardTitle>
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                        Default
                                    </Badge>
                                </div>
                                <CardDescription className="mt-1">
                                    {usingOrgDefault
                                        ? `${appName} uses the organization's Stripe Connect account for payouts. Revenue from this application will be settled to the shared billing account.`
                                        : `No billing account has been configured. Set up a Stripe Connect account at the organization level, or configure one specifically for ${appName}.`
                                    }
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {usingOrgDefault && resolvedStatus?.account && (
                            <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Stripe Account</p>
                                        <p className="mt-1 font-mono text-slate-700">{resolvedStatus.account.stripe_account_id}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Charges</p>
                                        <p className="mt-1 text-slate-700">{resolvedStatus.account.charges_enabled ? 'Enabled' : 'Disabled'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Payouts</p>
                                        <p className="mt-1 text-slate-700">{resolvedStatus.account.payouts_enabled ? 'Enabled' : 'Disabled'}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            {canManage && (
                                <Button variant="outline" onClick={handleSwitchToCustom}>
                                    <Settings className="h-4 w-4 mr-2" />
                                    Configure Custom Billing
                                </Button>
                            )}
                            {!usingOrgDefault && (
                                <Button variant="outline" asChild>
                                    <Link to="/settings/billing">
                                        Set Up Organization Billing
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Link>
                                </Button>
                            )}
                        </div>
                        <p className="text-xs text-slate-400 mt-3">
                            Configuring a custom billing account will isolate this application's revenue into its own Stripe Connect account.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {!hasAppLevelAccount && (
                <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                        <Settings className="h-4 w-4" />
                        <span>Configuring a custom billing account for {appName}</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setWantsCustom(false)}
                        className="text-blue-700 hover:text-blue-800 hover:bg-blue-100"
                    >
                        Use Default Instead
                    </Button>
                </div>
            )}
            <ConnectPanel
                connectState={connectState}
                stripeConnectInstance={stripeConnectInstance}
                fetchConnectStatus={fetchConnectStatus}
                onSetUpPayments={handleSetUpPayments}
                onboarding={onboarding}
                canManage={canManage}
                entityName={appName}
                setupDescription={`Link a Stripe account to ${appName} to receive payouts when other organizations subscribe to your published capabilities.`}
                setupFootnote="This will create a dedicated Stripe Connect account for this application's revenue."
            />
        </div>
    );
}
